import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bell,
  CheckCircle,
  FileText,
  Phone,
} from "@phosphor-icons/react";

import { THEME_COLORS } from "@/lib/themeColors";


function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}


function notificationUnread(notification = {}) {
  return (
    !notification.read
    && !notification.readAt
  );
}


function notificationTitle(notification = {}) {
  const title = (
    notification.title
    || notification.type
    || notification.eventType
    || ""
  );

  const clean =
    String(title).toLowerCase();

  if (
    clean.includes("quote")
    || clean.includes("devis")
  ) {
    return (
      notification.title
      || "Notification devis"
    );
  }

  if (
    clean.includes("follow")
    || clean.includes("relance")
  ) {
    return (
      notification.title
      || "Relance à traiter"
    );
  }

  if (clean.includes("message")) {
    return (
      notification.title
      || "Message reçu"
    );
  }

  if (clean.includes("document")) {
    return (
      notification.title
      || "Document ouvert"
    );
  }

  return (
    notification.title
    || "Notification"
  );
}


function notificationBody(notification = {}) {
  return (
    notification.message
    || notification.body
    || notification.content
    || notification.description
    || "Nouvelle activité à consulter."
  );
}


function formatRelativeDate(value) {
  if (!value) {
    return "Date inconnue";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Date inconnue";
  }

  const minutes = Math.floor(
    (Date.now() - date.getTime())
    / 60000
  );

  if (minutes < 1) {
    return "à l’instant";
  }

  if (minutes < 60) {
    return `il y a ${minutes} min`;
  }

  const hours =
    Math.floor(minutes / 60);

  if (hours < 24) {
    return `il y a ${hours} h`;
  }

  const days =
    Math.floor(hours / 24);

  if (days < 7) {
    return `il y a ${days} j`;
  }

  return date.toLocaleDateString(
    "fr-FR",
    {
      day: "2-digit",
      month: "short",
    }
  );
}


function NotificationIcon({
  notification,
}) {
  const haystack = (
    `${notificationTitle(notification)} `
    + notificationBody(notification)
  ).toLowerCase();

  let Icon = Bell;
  let color = THEME_COLORS.primary;

  if (
    haystack.includes("devis")
    || haystack.includes("validation")
  ) {
    Icon = FileText;
    color = THEME_COLORS.warning;
  } else if (
    haystack.includes("relance")
  ) {
    Icon = Phone;
    color = THEME_COLORS.danger;
  } else if (
    haystack.includes("message")
  ) {
    Icon = Bell;
    color = THEME_COLORS.violet;
  } else if (
    haystack.includes("accept")
  ) {
    Icon = CheckCircle;
    color = THEME_COLORS.success;
  }

  return (
    <span
      className="
        flex h-11 w-11 shrink-0
        items-center justify-center
        rounded-2xl
        bg-[var(--do-accent-soft)]
      "
    >
      <Icon
        size={20}
        weight="duotone"
        style={{ color }}
      />
    </span>
  );
}


export default function DashboardNotificationButton({
  notifications,
  unreadCount,
  open,
  onToggle,
  onClose,
  onMarkAllRead,
}) {
  const [filter, setFilter] =
    useState("all");

  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutside = (event) => {
      if (
        wrapperRef.current
        && !wrapperRef.current.contains(
          event.target
        )
      ) {
        onClose();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutside
    );

    document.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutside
      );

      document.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [
    open,
    onClose,
  ]);

  const rows = safeArray(
    notifications
  )
    .filter((notification) => (
      filter === "unread"
        ? notificationUnread(notification)
        : true
    ))
    .slice(0, 6);

  return (
    <div
      ref={wrapperRef}
      className="relative"
    >
      <button
        type="button"
        onClick={onToggle}
        className="
          relative flex h-12 w-12
          items-center justify-center
          rounded-2xl border
          bg-[var(--do-bg-solid)]
          shadow-sm transition
          hover:shadow-md
        "
        style={{
          borderColor:
            "var(--do-border)",
          color:
            "var(--do-ink)",
        }}
        data-testid="dashboard-notification-bell"
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell
          size={22}
          weight="duotone"
        />

        {unreadCount > 0 && (
          <span
            className="
              absolute -right-1 -top-1
              flex h-5 min-w-5
              items-center justify-center
              rounded-full
              bg-[var(--do-accent-strong)]
              px-1 text-xs font-black
              text-[var(--do-on-accent)]
            "
          >
            {unreadCount > 9
              ? "9+"
              : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <section
          className="
            absolute right-0 top-14 z-50
            w-[min(460px,calc(100vw-2rem))]
            rounded-[28px] border
            bg-[var(--do-bg-solid)]
            p-5 shadow-2xl
          "
          style={{
            borderColor:
              "var(--do-border)",
          }}
          data-testid="dashboard-notifications-popover"
        >
          <div
            className="
              flex items-start
              justify-between gap-3
            "
          >
            <div>
              <h2
                className="
                  font-display
                  text-2xl tracking-tight
                "
                style={{
                  color:
                    "var(--do-ink)",
                }}
              >
                Notifications
              </h2>

              <p
                className="mt-1 text-xs"
                style={{
                  color:
                    "var(--do-muted)",
                }}
              >
                Les actions récentes à suivre.
              </p>
            </div>

            {unreadCount > 0 && (
              <button
                type="button"
                onClick={onMarkAllRead}
                className="
                  rounded-2xl px-3 py-2
                  text-xs font-black
                  text-[var(--do-accent)]
                  hover:bg-[var(--do-accent-soft)]
                "
                data-testid="dashboard-notifications-mark-all-read"
              >
                Tout marquer comme lu
              </button>
            )}
          </div>

          <div
            className="
              mt-4 inline-flex
              rounded-2xl
              bg-[var(--do-bg-2)]
              p-1
            "
          >
            <button
              type="button"
              onClick={() =>
                setFilter("all")
              }
              className={`
                rounded-xl px-4 py-2
                text-xs font-black
                ${
                  filter === "all"
                    ? "bg-[var(--do-bg-solid)] text-[var(--do-accent)] shadow-sm"
                    : "text-[var(--do-muted)]"
                }
              `}
            >
              Toutes
            </button>

            <button
              type="button"
              onClick={() =>
                setFilter("unread")
              }
              className={`
                rounded-xl px-4 py-2
                text-xs font-black
                ${
                  filter === "unread"
                    ? "bg-[var(--do-bg-solid)] text-[var(--do-accent)] shadow-sm"
                    : "text-[var(--do-muted)]"
                }
              `}
            >
              Non lues
            </button>
          </div>

          <div
            className="
              mt-5 divide-y
              divide-[var(--do-border)]
            "
          >
            {rows.length === 0 ? (
              <div
                className="
                  py-8 text-center
                  text-sm
                  text-[var(--do-muted)]
                "
              >
                Aucune notification à afficher.
              </div>
            ) : (
              rows.map(
                (notification, index) => (
                  <article
                    key={
                      notification.id
                      || index
                    }
                    className="
                      flex gap-4 py-4
                      first:pt-0
                      last:pb-0
                    "
                    data-testid="dashboard-notification-item"
                  >
                    <NotificationIcon
                      notification={
                        notification
                      }
                    />

                    <div
                      className="
                        min-w-0 flex-1
                      "
                    >
                      <div
                        className="
                          flex items-start
                          justify-between
                          gap-3
                        "
                      >
                        <h3
                          className="
                            text-sm
                            font-black
                            leading-5
                          "
                          style={{
                            color:
                              "var(--do-ink)",
                          }}
                        >
                          {notificationTitle(
                            notification
                          )}
                        </h3>

                        <time
                          className="
                            shrink-0
                            text-xs
                            font-semibold
                          "
                          style={{
                            color:
                              "var(--do-muted)",
                          }}
                        >
                          {formatRelativeDate(
                            notification.createdAt
                          )}
                        </time>
                      </div>

                      <p
                        className="
                          mt-1 text-sm
                          leading-6
                        "
                        style={{
                          color:
                            "var(--do-ink-soft)",
                        }}
                      >
                        {notificationBody(
                          notification
                        )}
                      </p>
                    </div>

                    {notificationUnread(
                      notification
                    ) && (
                      <span
                        className="
                          mt-2 h-2 w-2
                          shrink-0
                          rounded-full
                          bg-[var(--do-accent)]
                        "
                      />
                    )}
                  </article>
                )
              )
            )}
          </div>

          <Link
            to="/app/settings"
            className="
              mt-3 flex items-center
              justify-center gap-2
              border-t pt-4
              text-sm font-black
              text-[var(--do-accent)]
            "
            style={{
              borderColor:
                "var(--do-border)",
            }}
          >
            Voir les paramètres notifications
            <ArrowRight
              size={15}
              weight="bold"
            />
          </Link>
        </section>
      )}
    </div>
  );
}
